/**
 * ab.hc.tools attribution guard (07.09.2026).
 *
 * Why: a url_redirect experiment is only "assigned" on its origin page
 * (/collections/viraka). When the control visitor walks on to a product page
 * and adds to cart, ab.js writes `_ab_assignments` with the assignments it
 * sees on THAT page — the experiment is missing, and the order can no longer
 * be attributed to the control arm (verified 07.09: cart held only the
 * site-wide e2e experiment). This script remembers every assignment the
 * visitor has ever been given (localStorage) and, after any cart mutation,
 * reconciles the cart's `_ab_visitor` / `_ab_assignments` attributes to the
 * union — the same two attributes ab.js writes, nothing else.
 *
 * Also exposes window.aflHcabAssignments() for the Seen buy box, whose cart
 * permalink carries the attributes itself (ab.js never sees a permalink).
 *
 * Safe by construction: only runs when ab.js is present, only touches a cart
 * that has lines, skips when the cart already carries the union, and never
 * fires from its own /cart/update.js call.
 */
(function () {
  'use strict';
  var KEY = 'afl_hcab_assignments';
  var pending = null;

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  function stored() {
    try {
      return JSON.parse(window.localStorage.getItem(KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  /* union of remembered + current assignments, persisted */
  function merge() {
    var hcab = window.__HCAB;
    var current = hcab && hcab.assignments ? hcab.assignments : {};
    var all = stored();
    Object.keys(current).forEach(function (id) {
      all[id] = current[id];
    });
    try {
      window.localStorage.setItem(KEY, JSON.stringify(all));
    } catch (e) {
      /* private mode: the current page's assignments still apply */
    }
    return all;
  }

  function pairs(all) {
    return Object.keys(all)
      .map(function (id) {
        return id + ':' + all[id];
      })
      .join(',');
  }

  function sync() {
    pending = null;
    var vid = cookie('_hcab_vid');
    var all = merge();
    var want = pairs(all);
    if (!vid || !want) return;
    window
      .fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        var a = cart.attributes || {};
        if (a._ab_visitor === vid && a._ab_assignments === want) return;
        return window.fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Afl-Hcab': '1' },
          body: JSON.stringify({ attributes: { _ab_visitor: vid, _ab_assignments: want } }),
        });
      })
      .catch(function () {});
  }

  function schedule() {
    if (pending) window.clearTimeout(pending);
    // after ab.js's own write (it writes once per page, right after the mutation)
    pending = window.setTimeout(sync, 1500);
  }

  if (!window.__HCAB_CTX && !window.__HCAB) return; // ab.js not installed on this page

  document.addEventListener('hcab:ready', merge);
  if (window.__HCAB && window.__HCAB.ready) merge();

  // any cart mutation through fetch — ours excluded — triggers one reconcile
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var p = nativeFetch.apply(this, arguments);
    try {
      var url = String(input && input.url ? input.url : input);
      var ours = init && init.headers && init.headers['X-Afl-Hcab'];
      if (!ours && /\/cart\/(add|update|change|clear)(\.js)?(\?|$)/.test(url)) {
        p.then(schedule, function () {});
      }
    } catch (e) {
      /* never break the caller */
    }
    return p;
  };

  // Horizon's cart events (form-POST and section-rendering paths) — same reconcile
  document.addEventListener('shopify:cart:lines-update', schedule);
  document.addEventListener('theme:cart:refresh', schedule);

  window.aflHcabAssignments = merge;
})();
